// src/services/AuthService.ts
import { Amplify } from '@aws-amplify/core';
import { signUp as amplifySignUp, confirmSignUp as amplifyConfirmSignUp, signIn as amplifySignIn, 
  signOut as amplifySignOut, getCurrentUser as amplifyGetCurrentUser, fetchAuthSession,
  resetPassword, confirmResetPassword } from 'aws-amplify/auth';

/**
 * Sign up a new user
 * @param email User's email
 * @param password User's password
 * @param name User's name
 */
export const signUp = async (email: string, password: string, name: string) => {
  try {
    const result = await amplifySignUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          name
        }
      }
    });
    return result;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

/**
 * Confirm a user's sign up with verification code
 * @param email User's email
 * @param code Verification code
 */
export const confirmSignUp = async (email: string, code: string) => {
  try {
    const result = await amplifyConfirmSignUp({
      username: email,
      confirmationCode: code
    });
    return result;
  } catch (error) {
    console.error('Error confirming sign up:', error);
    throw error;
  }
};

/**
 * Sign in a user
 * @param email User's email
 * @param password User's password
 */
export const signIn = async (email: string, password: string) => {
  try {
    console.log('Signing in user:', email);
    const result = await amplifySignIn({
      username: email,
      password
    });
    console.log('Sign in successful, result:', result);
    
    // Verify we can get the session after sign in
    try {
      const session = await fetchAuthSession();
      console.log('Auth session after sign in:', {
        hasTokens: !!session.tokens,
        hasIdToken: !!(session.tokens && session.tokens.idToken),
        hasAccessToken: !!(session.tokens && session.tokens.accessToken)
      });
    } catch (sessionError) {
      console.warn('Error fetching session after sign in:', sessionError);
    }
    
    return result;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  try {
    await amplifySignOut();
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get the current authenticated user
 */
export const getCurrentUser = async () => {
  try {
    const user = await amplifyGetCurrentUser();
    console.log('Current authenticated user:', user);
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Check if a user is authenticated
 */
export const isAuthenticated = async () => {
  try {
    // Try to get the current user
    try {
      const user = await amplifyGetCurrentUser();
      console.log('Authentication check - current user:', user);
      if (user) return true;
    } catch (userError) {
      console.warn('Error getting current user in isAuthenticated:', userError);
    }
    
    // Try to get the auth session as a backup check
    try {
      const session = await fetchAuthSession();
      console.log('Authentication check - session tokens available:', !!session.tokens);
      return !!(session && session.tokens);
    } catch (sessionError) {
      console.warn('Error getting auth session in isAuthenticated:', sessionError);
      return false;
    }
  } catch (error) {
    console.error('Error in isAuthenticated:', error);
    return false;
  }
};

/**
 * Request a password reset for a user
 * @param email User's email
 */
export const forgotPassword = async (email: string) => {
  try {
    const result = await resetPassword({ username: email });
    return result;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
};

/**
 * Complete the password reset process
 * @param email User's email
 * @param code Verification code
 * @param newPassword New password
 */
export const forgotPasswordSubmit = async (email: string, code: string, newPassword: string) => {
  try {
    const result = await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword
    });
    return result;
  } catch (error) {
    console.error('Error submitting new password:', error);
    throw error;
  }
};

export default {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
  isAuthenticated,
  forgotPassword,
  forgotPasswordSubmit
};